import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { LogOut, Menu } from 'lucide-react';
import { navFor, NavContext } from './navConfig';
import Avatar from './Avatar';
import NotificationBell from './NotificationBell';
import type { ReactNode } from 'react';

export interface ShellUser {
  name: string;
  email?: string;
  initials: string;
  /** Account profile photo URL (Globus applicant record); absent → initials. */
  avatarUrl?: string;
}

interface HeaderProps {
  brandName: string;
  programName?: string;
  season?: string;
  logo: ReactNode;
  user: ShellUser;
  navCtx: NavContext;
  onOpenDrawer: () => void;
  onSignOut: () => void;
}

/**
 * Fixed app header (PRD §6.4.3): 56px on mobile (hamburger · logo), 64px from
 * tablet up (logo · avatar dropdown). Notification bell (AP-13) sits between
 * the spacer and the avatar menu.
 */
export default function Header({ brandName, programName, season, logo, user, navCtx, onOpenDrawer, onSignOut }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the avatar dropdown on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const dropdownItems = navFor('dropdown', navCtx);

  return (
    <header className="appheader">
      <button className="iconbtn hamburger" aria-label="Open menu" onClick={onOpenDrawer}>
        <Menu size={22} aria-hidden />
      </button>

      <Link to="/dashboard" className="brandlink" aria-label={`${brandName} home`}>
        {logo}
      </Link>

      {programName && (
        <div className="headerprogram">
          <span className="hp-name">{programName}</span>
          {season && <span className="hp-season">{season}</span>}
        </div>
      )}

      <span className="spacer" />

      {/* Notification bell (AP-13) — polls unread count every 60 s */}
      <NotificationBell />

      <div className="avatarmenu" ref={menuRef}>
        <button
          className="iconbtn menutrigger"
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <Menu size={20} aria-hidden />
        </button>
        {menuOpen && (
          <div className="menu" role="menu">
            <div className="menuhead">
              <Avatar user={user} />
              <div>
                <div className="menuname">{user.name}</div>
                {user.email && <div className="menuemail">{user.email}</div>}
              </div>
            </div>
            {dropdownItems.map(({ key, label, to, Icon }) => (
              <NavLink key={key} to={to} role="menuitem" onClick={() => setMenuOpen(false)}>
                <Icon size={17} aria-hidden /> {label}
              </NavLink>
            ))}
            <button role="menuitem" className="menusignout" onClick={onSignOut}>
              <LogOut size={17} aria-hidden /> Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
